import { CommandTooltip } from '@modules/common/components/RichTextEditor/CommandTooltip';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import {
  Box,
  Button,
  ButtonProps,
  IconButton,
  Stack,
  TextField,
} from '@mui/material';
import Menu from '@mui/material/Menu';
import { useCurrentEditor } from '@tiptap/react';
import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { isStringValidUrl } from 'v2/utils/isStringValidUrl';

interface LinkButtonProps extends ButtonProps {
  closeBubbleMenu?: () => void;
}

export function LinkButton({
  closeBubbleMenu,
  ...buttonProps
}: LinkButtonProps) {
  const { editor } = useCurrentEditor();
  const [url, setURL] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!editor || !open) {
      return;
    }

    if (editor && editor.isActive('link')) {
      const selectedNodeAttributes = editor.getAttributes('link');

      if (selectedNodeAttributes && selectedNodeAttributes.href) {
        setURL(selectedNodeAttributes.href);
      }
    } else {
      setURL('');
    }
  }, [editor, open]);

  const setLink = useCallback(() => {
    if (!editor) {
      return;
    }

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();

      return;
    }

    // update link
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor, url]);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    closeBubbleMenu?.();
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!(value.startsWith(`http`) || value.startsWith(`https`))) {
      setErrorMessage(`URL must start with either "http" or "https"`);
    } else if (!isStringValidUrl(value)) {
      setErrorMessage('Please input a valid URL');
    } else {
      setErrorMessage('');
    }

    setURL(e.target.value);
  };

  const insertLink = () => {
    if (!url.trim()) {
      return;
    }

    closeMenu();
    setLink();
  };

  if (!editor) {
    return null;
  }

  const shouldShowRemoveLinkButton =
    editor.isActive('link') && !!url.trim() && isStringValidUrl(url);

  return (
    <Box>
      <CommandTooltip name="Link">
        <IconButton {...buttonProps} onClick={openMenu}>
          <InsertLinkIcon
            fontSize="small"
            sx={{ transform: 'rotate(135deg)' }}
          />
        </IconButton>
      </CommandTooltip>

      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        <Stack
          component={shouldShowRemoveLinkButton ? 'div' : 'form'}
          spacing={1}
          sx={{ p: 1, minWidth: '300px' }}
          onSubmit={(eve: FormEvent) => {
            eve.preventDefault();

            insertLink();
          }}
        >
          <TextField
            autoFocus
            value={url}
            variant="outlined"
            fullWidth
            size="small"
            placeholder="https://enterpret.com/"
            error={!!errorMessage}
            helperText={errorMessage}
            onChange={onChange}
          />
          {shouldShowRemoveLinkButton ? (
            <Button
              variant="outlined"
              onClick={() => {
                closeMenu();
                editor.chain().focus().unsetLink().run();
              }}
            >
              Remove Link
            </Button>
          ) : null}
          <Button
            type={shouldShowRemoveLinkButton ? 'button' : 'submit'}
            color="primary"
            variant="contained"
            startIcon={<InsertLinkIcon sx={{ transform: 'rotate(135deg)' }} />}
            disabled={!url.trim()}
            onClick={insertLink}
          >
            Link
          </Button>
        </Stack>
      </Menu>
    </Box>
  );
}
